<template>
  <div class="ai-assistant-panel" :class="{ compact }">
    <div class="question-card">
      <label :for="textareaId">Kysymys</label>
      <textarea
        :id="textareaId"
        v-model="question"
        :maxlength="maxLength"
        placeholder="Esim. Paljonko Bonzai Swamp keräsi pisteitä kesäkuussa?"
        :rows="compact ? 3 : 5"
      />
      <div class="question-footer">
        <span>{{ question.length }} / {{ maxLength }}</span>
        <button class="ask-button" :disabled="!canAsk" @click="askAssistant">
          {{ loading ? 'Haetaan...' : 'Kysy apurilta' }}
        </button>
      </div>

      <div class="examples" aria-label="Esimerkkikysymykset">
        <button v-for="example in examples" :key="example" type="button" @click="useExample(example)">
          {{ example }}
        </button>
      </div>
    </div>

    <div class="answer-card" :class="{ empty: !answer && !error }">
      <h2>Vastaus</h2>
      <div v-if="loading" class="loading-state">Raviremmi-apuri lukee dataa...</div>
      <div v-else-if="error" class="error-state">{{ error }}</div>
      <div v-else-if="answer" class="answer-text">{{ answer }}</div>
      <div v-else class="empty-state">
        Kysy esimerkiksi hevosen pisteistä, ohjastajan vireestä, omasta eilisestä pistesaaliista tai jakson kärkitilastoista.
      </div>

      <div v-if="intentLabel" class="intent-pill">
        Tulkinta: {{ intentLabel }}
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';
import axios from '../plugins/axios';

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

export default {
  name: 'AiAssistantPanel',
  props: {
    compact: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const maxLength = 600;
    const question = ref('');
    const answer = ref('');
    const error = ref('');
    const loading = ref(false);
    const lastIntent = ref(null);
    const textareaId = `aiQuestion-${Math.random().toString(36).slice(2)}`;

    const examples = [
      'Näytä jakson parhaat pelaajat, hevoset ja ohjastajat.',
      'Paljonko Bonzai Swamp keräsi pisteitä kesäkuussa?',
      'Miten omat pisteeni muodostuivat eilen?',
    ];

    const canAsk = computed(() => question.value.trim().length > 0 && !loading.value);
    const intentLabel = computed(() => {
      const intent = lastIntent.value?.intent;
      const labels = {
        player_points_period: 'pelaajan pisteet aikavälillä',
        horse_points_period: 'hevosen pisteet aikavälillä',
        jockey_points_period: 'ohjastajan pisteet aikavälillä',
        my_daily_breakdown: 'oma päivän piste-erittely',
        period_top_stats: 'jakson kärkitilastot',
        needs_clarification: 'tarvitsee tarkennuksen',
        unknown: 'ei vielä tuettu',
        not_found: 'dataa ei löytynyt',
      };
      return labels[intent] || '';
    });

    const useExample = (example) => {
      question.value = example;
      answer.value = '';
      error.value = '';
      lastIntent.value = null;
    };

    const askAssistant = async () => {
      if (!canAsk.value) return;
      loading.value = true;
      answer.value = '';
      error.value = '';
      lastIntent.value = null;

      try {
        const response = await axios.post(`${API_BASE_URL}/ai/query`, {
          question: question.value.trim(),
        });
        answer.value = response.data?.answer || 'Apuri ei palauttanut vastausta.';
        lastIntent.value = response.data?.intent || null;
      } catch (err) {
        error.value = err.response?.data?.message || 'Raviremmi-apuri ei vastaa juuri nyt. Yritä hetken päästä uudelleen.';
      } finally {
        loading.value = false;
      }
    };

    return {
      maxLength,
      question,
      answer,
      error,
      loading,
      examples,
      canAsk,
      intentLabel,
      textareaId,
      useExample,
      askAssistant,
    };
  },
};
</script>

<style scoped>
.ai-assistant-panel {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 0.85fr);
  gap: 18px;
}

.ai-assistant-panel.compact {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.question-card,
.answer-card {
  border: 1px solid #1e3a5f;
  border-radius: 16px;
  background: #112240;
  padding: 18px;
}

.compact .question-card,
.compact .answer-card {
  padding: 14px;
}

.question-card label,
.answer-card h2 {
  display: block;
  margin: 0 0 10px;
  color: #ffffff;
  font-weight: 800;
}

.answer-card h2 {
  font-size: 1rem;
}

textarea {
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  border: 1px solid #2d5485;
  border-radius: 14px;
  padding: 14px;
  color: #e6f1ff;
  background: #0a192f;
  font: inherit;
  line-height: 1.5;
}

textarea:focus {
  outline: none;
  border-color: #64ffda;
  box-shadow: 0 0 0 3px rgba(100, 255, 218, 0.14);
}

.question-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  color: #8892b0;
  font-size: 0.9rem;
}

.ask-button {
  border: none;
  border-radius: 999px;
  padding: 10px 16px;
  color: #0a192f;
  background: #64ffda;
  font-weight: 900;
  cursor: pointer;
}

.ask-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.examples {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.examples button {
  border: 1px solid rgba(100, 255, 218, 0.22);
  border-radius: 999px;
  padding: 8px 10px;
  color: #b7c6dc;
  background: rgba(10, 25, 47, 0.75);
  cursor: pointer;
  font-size: 0.88rem;
}

.examples button:hover {
  color: #64ffda;
  border-color: rgba(100, 255, 218, 0.48);
}

.answer-card {
  min-height: 220px;
}

.compact .answer-card {
  min-height: 120px;
}

.answer-card.empty {
  border-style: dashed;
}

.answer-text,
.empty-state,
.loading-state,
.error-state {
  white-space: pre-wrap;
  line-height: 1.65;
  color: #d6e6f8;
}

.empty-state,
.loading-state {
  color: #8892b0;
}

.error-state {
  color: #ffb4a8;
}

.intent-pill {
  display: inline-flex;
  margin-top: 14px;
  padding: 7px 10px;
  border-radius: 999px;
  color: #ffc65c;
  background: rgba(255, 198, 92, 0.12);
  font-size: 0.85rem;
  font-weight: 800;
}

@media (max-width: 820px) {
  .ai-assistant-panel {
    grid-template-columns: 1fr;
  }

  .question-footer {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
